import { LockKeyhole, TriangleAlert } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { BottomRightLaneSlot } from "@/components/layout/BottomRightLane";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  hasLegacyBillingAccess,
  useBillingStatus,
  type BillingStatusContextValue,
} from "../BillingStatusContext";

function billingNoticeHidden(billing: BillingStatusContextValue, pathname: string) {
  if (hasLegacyBillingAccess(billing)) return true;
  if (billing.isLoading || pathname.startsWith("/billing")) return true;
  const subscription = billing.buildingSubscription;
  if (subscription?.billing_exempt === true && !subscription.activation_pending) return true;
  return !billing.isReadOnly && !billing.mutationStatusUnknown;
}

/**
 * The org-wide billing lockout, explained wherever the user happens to be.
 *
 * `BillingBanners` only renders inside the billing pages, and only from a
 * context that loaded — so read-only (every mutating control) and "status
 * unknown" (adding buildings) withheld things while saying nothing anywhere
 * outside /billing. A user on /buildings simply lost Add New Building and every
 * delete icon with no account of where they went.
 *
 * So this rides the shared bottom-right lane (the one owner of that corner) and
 * is mounted once, at the app shell, next to the router. It stands down on the
 * billing pages, where the fuller banners already say all this.
 */
export function BillingRestrictionNotice() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const billing = useBillingStatus();
  const { canManageOrgPermissions } = usePermissions();
  const copy = t.billing;

  // A cold load is not a lockout, and the billing pages explain themselves.
  if (billingNoticeHidden(billing, location.pathname)) return null;

  // Read-only is the settled verdict and outranks a status we simply could not
  // read, so only one card is ever shown.
  const notice = billing.isReadOnly
    ? {
        icon: <LockKeyhole className="h-4 w-4" />,
        title: copy.banners.readOnlyTitle,
        body: copy.banners.readOnlyBody,
        actionLabel: canManageOrgPermissions ? copy.banners.viewInvoice : undefined,
        onAction: () => navigate("/billing/invoices"),
      }
    : {
        icon: <TriangleAlert className="h-4 w-4" />,
        title: copy.shell.statusUnknownTitle,
        body: copy.shell.statusUnknownBody,
        actionLabel: copy.errors.retry,
        onAction: () => void billing.refetch(),
      };

  return (
    <BottomRightLaneSlot name="billingRestriction">
      <Alert variant="destructive" className="bg-background shadow-lg">
        {notice.icon}
        <AlertTitle>{notice.title}</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{notice.body}</p>
          {notice.actionLabel && (
            <Button variant="outline" size="sm" onClick={notice.onAction}>
              {notice.actionLabel}
            </Button>
          )}
        </AlertDescription>
      </Alert>
    </BottomRightLaneSlot>
  );
}
